import React, { useEffect, useState } from "react";
import { Box, Button, Tabs, Tab, Paper, Typography, Grid, Card, CardContent, Divider, FormControl } from "@mui/material";
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import { FormLabel } from '@mui/material';
import { GrDocumentText } from "react-icons/gr";
import EastIcon from '@mui/icons-material/East';


const DatesAndPrices = (packageItemInfo) => {
    const [organizedDates, setOrganizedDates] = useState({});
    const [months, setMonths] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [departureCity, setDepartureCity] = React.useState(''); // Start with empty string
    const [departureDate, setDepartureDate] = React.useState('');
    const [cityError, setCityError] = useState('');
    const [departureDateError, setDepartureDateError] = useState('');
    const [formattedDate, setFormattedDate] = useState('');
    const [packageDetails, setPackageDetails] = useState([]);
    const [selectedCityData, setSelectedCityData] = useState(null);
    const [basicPrice, setBasicPrice] = useState(0);
    const [joiningPrice, setJoiningPrice] = useState(0);
    const startDate = formattedDate;
    const totalDays = parseInt(packageItemInfo?.packageItemInfo?.packageDays) || 0;
    const parsedDate = new Date(formattedDate);
    parsedDate.setDate(parsedDate.getDate() + (totalDays - 1));
    const endDate = parsedDate.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });

    const joinObject = [{
        cityName: "Joining/Leaving",
        packageDateDetails: [],
    }]
    
    useEffect(() => {
        const details = packageItemInfo?.packageItemInfo;
        setPackageDetails(details);

        const roomDetails = details?.roomDetails;

        if (Array.isArray(roomDetails) && roomDetails.length > 0) {
            setBasicPrice((roomDetails[0].roomPrice) * 2);
            setJoiningPrice((roomDetails[0].packageCostForJoinLeave) * 2);
        }
    }, [packageItemInfo]);

    useEffect(() => {
        if (packageDetails?.departureDetails && packageDetails.departureDetails.length > 0 && !departureCity) {
            const mumbaiCity = packageDetails.departureDetails.find(city =>
                city.cityName === 'Mumbai'
            );
            if (mumbaiCity) {
                setDepartureCity(mumbaiCity.cityName);
            } else {
                setDepartureCity(packageDetails.departureDetails[0].cityName);
            }
        }
    }, [packageDetails, departureCity]);

    useEffect(() => {
        if (!departureCity || !packageDetails?.departureDetails) {
            setMonths([]);
            setOrganizedDates({});
            return;
        }

        // Find the selected city's data
        const cityData = packageDetails.departureDetails.find(
            (departure) => departure.cityName === departureCity
        );

        if (!cityData || !cityData.packageDateDetails) {
            setMonths([]);
            setOrganizedDates({});
            setSelectedCityData(null);
            return;
        }

        setSelectedCityData(cityData);

        const datesByMonth = {};
        const monthSet = new Set();
        const seenDates = new Set(); // Track unique dates to prevent duplicates
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        cityData.packageDateDetails.forEach(dateInfo => {
            const dateStr = dateInfo.fullDate; // "27/05/2025" format

            // Skip if we've already processed this date
            if (seenDates.has(dateStr)) {
                return;
            }

            const [day, month, year] = dateStr.split('/');
            const dateObj = new Date(`${year}-${month}-${day}`);

            if (dateObj < today) {
                return; // Skip past dates
            }

            // Add to seen dates set
            seenDates.add(dateStr);

            const monthKey = dateObj.toLocaleString('default', { month: 'long', year: 'numeric' });

            if (!datesByMonth[monthKey]) {
                datesByMonth[monthKey] = [];
                monthSet.add(monthKey);
            }

            const dayOfWeek = dateInfo.packageDay; // "Tuesday", "Saturday", etc.

            datesByMonth[monthKey].push({
                date: dateObj,
                day: parseInt(day),
                dayOfWeek: dayOfWeek.substring(0, 3), // Convert to short form like "Tue", "Sat"
                // price: `Rs.${defaultPrice}`, // You can add price logic here if needed
                fullFormatted: dateStr,
                packageDateId: dateInfo.packageDateId, // Store this for booking reference
            });
        });

        // Sort dates within each month
        Object.keys(datesByMonth).forEach(monthKey => {
            datesByMonth[monthKey].sort((a, b) => a.date - b.date);
        });

        // Sort months chronologically
        const sortedMonths = Array.from(monthSet).sort((a, b) => {
            const dateA = new Date(a);
            const dateB = new Date(b);
            return dateA - dateB;
        });

        setMonths(sortedMonths);
        setOrganizedDates(datesByMonth);
        if (sortedMonths.length > 0 && activeTab >= sortedMonths.length) {
            setActiveTab(0);
        }
    }, [departureCity, packageDetails]);

    useEffect(() => {
        if (!packageDetails?.departureDetails || !Array.isArray(packageDetails.departureDetails)) {
            return;
        }

        window.scrollTo(0, 0)

        packageDetails.departureDetails.forEach(element => {
            if (element?.packageDateDetails && Array.isArray(element.packageDateDetails)) {
                element.packageDateDetails.forEach(date => {
                    const alreadyExists = joinObject[0].packageDateDetails.some(
                        existing => existing.fullDate === date.fullDate
                    );
                    if (!alreadyExists) {
                        joinObject[0].packageDateDetails.push({
                            fullDate: date.fullDate,
                            packageDate: date.packageDate,
                            packageDay: date.packageDay,
                            packageMonth: date.packageMonth,
                            packageYear: date.packageYear
                        });
                    }
                });
            }
        });

        const cityName = joinObject[0].cityName;
        const alreadyAdded = packageDetails.departureDetails.some(
            detail => detail.cityName === cityName
        );

        if (!alreadyAdded) {
            packageDetails.departureDetails.push(joinObject[0]);
        }

    }, [packageDetails]);

    const handleChange = (event, newValue) => {
        setActiveTab(newValue)
    };

    const handleChangeCity = (event) => {
        const value = event.target.value
        if (!value) {
            setCityError('Please provide a valid information')
        } else {
            setCityError('')
        }
        setDepartureCity(value)
        setDepartureDate('')
        setFormattedDate('')
    }

    return (
        <div className="container payment-container" style={{ marginTop: '20px', gap: 3 }}>
            <div className="col-md-7">
                <Card sx={{ display: 'flex', flexDirection: 'column', padding: 2, borderRadius: 2, minHeight: '350px' }}>
                    <Typography sx={{ fontWeight: '600', gap: 0 }}>1.Departure City & Date</Typography>
                    <hr></hr>
                    <FormControl className="mt-3">
                        <FormLabel
                            id="departure-city-radio-group-label"
                            sx={{
                                fontSize: '16px',
                                fontWeight: 500,
                                color: '#2d2b29 !important',
                                mb: 2,
                                '&.Mui-focused': {
                                    color: '#2d2b29 !important'
                                }
                            }}
                        >
                            Departure Cities
                        </FormLabel>
                        <RadioGroup
                            aria-labelledby="departure-city-radio-group-label"
                            value={departureCity}
                            onChange={handleChangeCity}
                            sx={{
                                gap: 1,
                                flexDirection: 'row !important',
                                '& .MuiFormGroup-root': {
                                    flexDirection: 'row !important'
                                }
                            }}
                        >
                            {packageItemInfo.packageItemInfo?.departureDetails && packageItemInfo.packageItemInfo.departureDetails?.map((departure) => (
                                <FormControlLabel
                                    key={departure.departureId}
                                    value={departure.cityName}
                                    control={
                                        <Radio
                                            sx={{
                                                color: '#9ca3af', // Default gray color for unselected
                                                '&.Mui-checked': {
                                                    color: departure.cityName === 'Joining/Leaving' ? '#1976d2' : '#1976d2',
                                                },
                                                '& .MuiSvgIcon-root': {
                                                    fontSize: 20,
                                                }
                                            }}
                                        />
                                    }
                                    label={
                                        <span style={{
                                            textTransform: 'capitalize',
                                            fontSize: '14px',
                                            fontWeight: 500,
                                            color: '#333'
                                        }}>
                                            {departure.cityName}
                                        </span>
                                    }
                                    sx={{
                                        margin: 0,
                                        padding: '8px 12px',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '8px',
                                        backgroundColor: departureCity === departure.cityName ? '#f8fafc' : 'transparent',
                                        width: 'fit-content',
                                        minWidth: '200px',
                                        '&:hover': {
                                            backgroundColor: '#f1f5f9'
                                        },
                                        '& .MuiFormControlLabel-label': {
                                            flex: 1
                                        }
                                    }}
                                />
                            ))}
                        </RadioGroup>

                        {departureCity === 'Joining/Leaving' && (
                            <div className="mt-2" style={{
                                fontSize: '12px',
                                fontWeight: 500,
                                backgroundColor: '#ebf6ff',
                                width: 'fit-content',
                                padding: '8px 12px',
                                borderRadius: '6px',
                                border: '1px solid #dbeafe'
                            }}>
                                *Book your own flights/Trains and join directly at the first destination of the tour
                            </div>
                        )}

                        {cityError && (
                            <p style={{
                                color: '#dc2626',
                                fontSize: '12px',
                                marginTop: '8px',
                                marginBottom: 0
                            }}>
                                {cityError}
                            </p>
                        )}
                    </FormControl>
                    <Box sx={{ mt: 2 }}>
                        <Box borderBottom={1} borderColor="divider" overflow="auto">
                            <Tabs
                                value={activeTab}
                                onChange={(e, newValue) => handleChange(e, newValue)}
                                variant="scrollable"
                                scrollButtons="auto"
                                aria-label="Month Tabs"
                            >
                                {months.map((month, index) => (
                                    <Tab
                                        key={month}
                                        label={month}
                                        id={`month-tab-${index}`}
                                        aria-controls={`month-tabpanel-${index}`}
                                    />
                                ))}
                            </Tabs>
                        </Box>
                        <Box>
                            <Grid container spacing={2} mt={1}>
                                {months[activeTab] &&
                                    organizedDates[months[activeTab]]?.map((dateInfo, index) => (
                                        <Grid item key={index}>
                                            <Card
                                                sx={{
                                                    width: 96,
                                                    cursor: 'pointer',
                                                    p: 0,
                                                    border:
                                                        formattedDate === `${String(dateInfo.date.getDate()).padStart(2, '0')} ${["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][dateInfo.date.getMonth()]} ${dateInfo.date.getFullYear()}`
                                                            ? '2px solid #1976d2'
                                                            : '1px solid #e0e0e0',
                                                    borderRadius: 2,
                                                }}
                                                onClick={() => {
                                                    const selectedDate = dateInfo.date;
                                                    const dd = String(selectedDate.getDate()).padStart(2, '0');
                                                    const mm = selectedDate.getMonth(); // 0-based
                                                    const yyyy = selectedDate.getFullYear();

                                                    const monthNames = [
                                                        "Jan", "Feb", "Mar", "Apr", "May", "Jun",
                                                        "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
                                                    ];
                                                    const formatted = `${dd} ${monthNames[mm]} ${yyyy}`;

                                                    setDepartureDate(`${yyyy}-${String(mm + 1).padStart(2, '0')}-${dd}`);
                                                    setFormattedDate(formatted);
                                                    setDepartureDateError('');
                                                }}
                                            >
                                                <CardContent sx={{ p: 0 }}>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: "#f5f5f5",
                                                            textAlign: "center",
                                                            fontWeight: "500",
                                                            borderBottom: "1px solid #e0e0e0",
                                                            py: 1,
                                                        }}
                                                    >
                                                        {dateInfo.dayOfWeek}
                                                    </Box>
                                                    <Box
                                                        display="flex"
                                                        flexDirection="column"
                                                        justifyContent="center"
                                                        alignItems="center"
                                                        px={1.5}
                                                        mt={2}
                                                    >
                                                        <Typography variant="h6" fontWeight="bold">
                                                            {dateInfo.day}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary" mt={0.5}>
                                                            {dateInfo.price}
                                                        </Typography>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                {months.length === 0 && (
                                    <Typography sx={{ marginLeft: '20px' }} variant="body2" color="textSecondary" fontStyle="italic">
                                        Sorry...Currently no dates available for this city!
                                    </Typography>
                                )}
                            </Grid>
                            {departureDateError && <p style={{ color: 'red' }}>{departureDateError}</p>}
                        </Box>
                    </Box>
                </Card>
            </div>
            <div className="col-md-5">
                <Card sx={{ display: 'flex', flexDirection: 'column', padding: 2, borderRadius: 2, minHeight: '350px' }}>
                    <Typography sx={{ fontWeight: '600' }}><GrDocumentText className='me-2' />Selected Parameters</Typography>
                    <hr></hr>
                    <ul style={{ fontSize: '13px', listStyle: 'none', padding: 0, lineHeight: 3 }}>
                        <li className="d-flex flex-row">
                            <div className="col-md-4 col-4">Dept. City</div>
                            <div className="col-md-8 col-8" style={{ textTransform: 'capitalize' }}><strong>{departureCity || ''}</strong></div>
                        </li>
                        <li className="d-flex flex-row">
                            <div className="col-md-4 col-4">Dept. Date</div>
                            {formattedDate && (
                                <div className="col-md-8 col-8"><strong>{formattedDate}
                                    <EastIcon sx={{ fontSize: '16px' }} /> {endDate}</strong>
                                </div>
                            )}

                        </li>
                        <li className="d-flex flex-row">
                            <div className="col-md-4 col-4">Travellers</div>
                            <div className="col-md-8 col-8">
                                <div>Adults (0),Child(0),Infant(0)</div>
                            </div>
                        </li>
                        <li className="d-flex flex-row">
                            <div className="col-md-4 col-4">Rooms</div>
                            <div className="col-md-8 col-8">
                                <div>0 Rooms</div>
                            </div>
                        </li>
                    </ul>
                    <Divider></Divider>
                    <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginTop: '20px' }}>
                        <div className='d-flex align-items-start flex-column'>
                            <div className='text-start'>Basic Price</div>
                        </div>
                        <div className='d-flex flex-column align-items-end'>
                            <div style={{ fontSize: '18px', fontWeight: '600px', color: 'green', padding: 0, margin: 0 }}>&#8377; {departureCity !== 'Joining/Leaving' ? basicPrice : joiningPrice}</div>
                            <div style={{ fontSize: '10px', padding: 0, margin: 0 }}>Per person on twin sharing</div>
                        </div>
                    </Box>
                </Card>
            </div>

        </div>
    )
}

export default DatesAndPrices;